import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import { useFormContext } from 'react-hook-form';
import _, { isNil, uniq } from 'lodash';
import { t } from '@lingui/macro';

import DataTable from '../data-table/DataTable';
import { useArrayLoader, useQueryPostLoader } from '../data-table/Loaders';
import { useSelectorStyles } from './ListSelectorStyles';
import { SELECTIONMODE_MULTI, SELECTIONMODE_NONE } from '../data-table/SelectionContext';
import ListSelectorDialog from './ListSelectorDialog';
import { COLUMN_TYPES } from '../data-table/DataTableColumns';
import { ID_FIELD } from './FormConstants';
import NgpFormButton from './NgpFormButton';
import { filterEntities } from './ListSelectorUtils';
import { iconRenderer } from '../data-table/columns/Icon';

export default function ListSelector({
    gridColumns,
    selectedGridColumns,
    dbSelected = [],
    selectedDataSource,
    selectedLoaderFunction,
    availableDataSource,
    availableLoaderFunction,
    availableSelectAll,
    idsDataSource,
    fieldName,
    addButtonText,
    removeButtonText,
    setSelectionIsDirty,
    isMultiRemove = true,
    selectedEmptyGridText,
    updateSelectionFunction,
    resolveInvertedFunction,
    sorters,
    disabled = false,
    dataArray = null,
    dataArraySearchFields = [],
    hideActions = false,
    visibleRows = undefined,
    rowRemovalCondition,
    disableCSVExport = true,
    onAddAction,
    dialogMaxWidth,
    dialogFullWidth,
}) {
    const classes = useSelectorStyles();
    const { setValue, getValues } = useFormContext();
    const selected = useRef(dbSelected);
    const nonDeletableRows = useRef([]);

    const staticSelectedData = useMemo(() => {
        if (dataArray) {
            return dataArray.filter((dataItem) => selected.current.includes(dataItem._id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataArray, selected.current]);
    const staticDataFilterCallback = useCallback(
        (entities, filters) => filterEntities(entities, filters, dataArraySearchFields),
        [dataArraySearchFields]
    );
    const selectedLoaderFn = !isNil(dataArray) ? useArrayLoader : selectedLoaderFunction || useQueryPostLoader;
    const selectedLoader = !isNil(dataArray)
        ? selectedLoaderFn(staticSelectedData, staticDataFilterCallback)
        : selectedLoaderFn(selectedDataSource, selected.current, false);

    const [theSelectedGridColumns, setTheSelectedGridColumns] = useState(selectedGridColumns || gridColumns);
    const [removeDisabled, setRemoveDisabled] = useState(false);
    const [tableSelection, setTableSelection] = useState(undefined);
    const [showDialog, setShowDialog] = useState(false);

    useEffect(() => {
        const columns = selectedGridColumns || gridColumns;
        setTheSelectedGridColumns(
            disabled
                ? _.map([...columns], (theColumn) => _.merge(theColumn, { resizable: false }))
                : _.map([...deleteColumnDef, ...columns], (theColumn) => _.merge(theColumn, { resizable: false }))
        );
        // Only want to run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTableSelectionChange = (currentTableSelection) => {
        const hasDeletableSelections = getValues(fieldName)
            ? getValues(fieldName).length !== nonDeletableRows.current.length
            : false;

        setTableSelection(currentTableSelection);
        setRemoveDisabled(
            !currentTableSelection ||
                _.isEmpty(selected.current) ||
                (_.isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted) ||
                !hasDeletableSelections
        );
    };

    const selectionOptions =
        !disabled && isMultiRemove
            ? {
                  mode: SELECTIONMODE_MULTI,
                  allowSelectAll: true,
                  idMapping: ID_FIELD,
                  onChange: handleTableSelectionChange,
              }
            : { mode: SELECTIONMODE_NONE };

    const updateSelection = (selectedIds) => {
        selected.current = selectedIds;

        if (_.isFunction(updateSelectionFunction)) {
            updateSelectionFunction(selectedIds);
        } else {
            setValue(fieldName, selectedIds);
        }

        if (_.isFunction(setSelectionIsDirty)) {
            setSelectionIsDirty(true);
        }
    };

    const removeSelection = () => {
        updateSelection(
            _.filter(selected.current, (record) => {
                const inSelectionList = _.includes(tableSelection.selection, record);
                return tableSelection.inverted ? inSelectionList : !inSelectionList;
            })
        );
    };

    const removeOne = (record, cfg, e) => {
        // Stop the icon click from flashing the select box on the item in the table when multi delete is enabled
        e.stopPropagation();

        updateSelection(_.without(selected.current, record[ID_FIELD]));
    };

    const handleAddClick = () => {
        if (_.isFunction(onAddAction)) {
            onAddAction({
                selectedIds: [...selected.current],
                updateSelection,
                openDefaultDialog: () => setShowDialog(true),
            });
            return;
        }

        setShowDialog(true);
    };

    const deleteColumnDef = [
        {
            type: COLUMN_TYPES.ICON,
            title: '',
            width: 45,
            renderer: (row, column) => {
                if (rowRemovalCondition) {
                    if (!rowRemovalCondition(row))
                        nonDeletableRows.current = uniq([...nonDeletableRows.current, row._id]);
                    return rowRemovalCondition(row) ? iconRenderer(row, column) : null;
                }

                return iconRenderer(row, column);
            },
            icons: [
                {
                    tooltip: t`Delete`,
                    getIcon: (data) => {
                        // This is for the nPoint selector on sites detail, but I think it should work for everything
                        //  For sites, we blend a list of agents that are manually selected as well as agents that
                        //  are part of an IP scope, you can't delete the ones that are part of an IP scope.
                        if (_.indexOf(selected.current, data._id) > -1) {
                            return <DeleteIcon />;
                        }
                    },
                    clickHandler: removeOne,
                },
            ],
        },
    ];

    return (
        <>
            <div className={classes.selectorContainer}>
                {!hideActions && (
                    <div className={classes.selectorActions}>
                        <NgpFormButton onClick={handleAddClick} disabled={disabled}>
                            {addButtonText}
                        </NgpFormButton>
                        {isMultiRemove && (
                            <NgpFormButton onClick={removeSelection} disabled={removeDisabled}>
                                {removeButtonText}
                            </NgpFormButton>
                        )}
                    </div>
                )}
                <DataTable
                    loader={selectedLoader}
                    columns={theSelectedGridColumns}
                    showSelectionColumn={!disabled && isMultiRemove}
                    disableCSVExport={disableCSVExport}
                    disableColumnRemover
                    sorters={sorters || selectedDataSource?.initialSort}
                    selectionOptions={selectionOptions}
                    emptyText={selectedEmptyGridText}
                    selectionColumnRenderCondition={rowRemovalCondition}
                    visibleRows={visibleRows}
                />
            </div>
            <ListSelectorDialog
                gridColumns={gridColumns}
                availableDataSource={availableDataSource}
                availableLoaderFunction={availableLoaderFunction}
                availableSelectAll={availableSelectAll}
                idsDataSource={idsDataSource}
                addButtonText={addButtonText}
                showDialog={showDialog}
                setShowDialog={setShowDialog}
                selected={selected.current}
                updateSelection={updateSelection}
                resolveInvertedFunction={resolveInvertedFunction}
                sorters={sorters}
                dataArray={dataArray}
                dataArraySearchFields={dataArraySearchFields}
                dialogMaxWidth={dialogMaxWidth}
                dialogFullWidth={dialogFullWidth}
            />
        </>
    );
}
